/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const pipeMaterials: Record<string, { costPerFt: number; label: string }> = {
  copper_half: { costPerFt: 4.5, label: '1/2" Copper' },
  copper_three_quarter: { costPerFt: 6.0, label: '3/4" Copper' },
  copper_one: { costPerFt: 9.5, label: '1" Copper' },
  pex_half: { costPerFt: 0.75, label: '1/2" PEX' },
  pex_three_quarter: { costPerFt: 1.1, label: '3/4" PEX' },
  cpvc_half: { costPerFt: 0.6, label: '1/2" CPVC' },
  cpvc_three_quarter: { costPerFt: 0.85, label: '3/4" CPVC' },
  pvc_two: { costPerFt: 1.5, label: '2" PVC (drain)' },
  pvc_three: { costPerFt: 2.8, label: '3" PVC (drain)' },
  pvc_four: { costPerFt: 4.2, label: '4" PVC (drain)' },
};

const fixtureRoughInHours: Record<string, { hours: number; label: string }> = {
  toilet: { hours: 3.0, label: "Toilet" },
  sink_bathroom: { hours: 2.5, label: "Bathroom Sink" },
  sink_kitchen: { hours: 3.5, label: "Kitchen Sink" },
  bathtub: { hours: 4.0, label: "Bathtub / Shower" },
  dishwasher: { hours: 1.5, label: "Dishwasher Connection" },
  washing_machine: { hours: 2.0, label: "Washing Machine Box" },
  water_heater: { hours: 4.0, label: "Water Heater" },
  hose_bib: { hours: 1.5, label: "Hose Bib (Outdoor)" },
};

export function PlumbingCalculatorClient() {
  const [pipeType, setPipeType] = useState("copper_three_quarter");
  const [pipeLength, setPipeLength] = useState("");
  const [fittingsCount, setFittingsCount] = useState("");
  const [fixtures, setFixtures] = useState<Record<string, string>>({});
  const [laborRate, setLaborRate] = useState("75");
  const [wasteFactor, setWasteFactor] = useState("10");

  const pipe = pipeMaterials[pipeType];
  const length = parseFloat(pipeLength) || 0;
  const fittings = parseInt(fittingsCount) || 0;
  const rate = parseFloat(laborRate) || 75;
  const waste = parseFloat(wasteFactor) || 10;

  // Pipe material cost
  const pipeCost = length * (pipe?.costPerFt ?? 0) * (1 + waste / 100);

  // Fittings (avg $3-8 per fitting depending on material)
  const avgFittingCost = pipeType.startsWith("copper") ? 6.5 : 2.5;
  const fittingsCost = fittings * avgFittingCost;

  // Fixture labor hours
  let totalFixtureHours = 0;
  Object.entries(fixtures).forEach(([key, count]) => {
    const qty = parseInt(count) || 0;
    const fixture = fixtureRoughInHours[key];
    if (fixture) totalFixtureHours += qty * fixture.hours;
  });

  // Pipe installation labor (rough: 8-15 LF per hour depending on material)
  const pipeHoursPerFt = pipeType.startsWith("copper") ? 1 / 8 : 1 / 12;
  const pipeLaborHours = length * pipeHoursPerFt;

  const totalLaborHours = pipeLaborHours + totalFixtureHours;
  const laborCost = totalLaborHours * rate;
  const materialCost = pipeCost + fittingsCost;
  const totalCost = laborCost + materialCost;

  const hasResult = length > 0 || totalFixtureHours > 0;

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const fmtDec = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        Pipe Materials
      </h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="block text-sm font-medium text-foreground">
            Pipe Type
          </label>
          <select
            value={pipeType}
            onChange={(e) => setPipeType(e.target.value)}
            className={inputClass}
          >
            {Object.entries(pipeMaterials).map(([key, mat]) => (
              <option key={key} value={key}>
                {mat.label} (${mat.costPerFt.toFixed(2)}/ft)
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Total Pipe Run (ft)
          </label>
          <input
            type="number"
            value={pipeLength}
            onChange={(e) => setPipeLength(e.target.value)}
            placeholder="150"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Number of Fittings
          </label>
          <input
            type="number"
            value={fittingsCount}
            onChange={(e) => setFittingsCount(e.target.value)}
            placeholder="25"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">
            Waste Factor (%)
          </label>
          <input
            type="number"
            value={wasteFactor}
            onChange={(e) => setWasteFactor(e.target.value)}
            placeholder="10"
            className={inputClass}
          />
        </div>
      </div>

      <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">
        Fixture Rough-Ins
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(fixtureRoughInHours).map(([key, fixture]) => (
          <div key={key}>
            <label className="block text-sm font-medium text-foreground">
              {fixture.label} ({fixture.hours} hrs ea)
            </label>
            <input
              type="number"
              value={fixtures[key] || ""}
              onChange={(e) =>
                setFixtures((prev) => ({ ...prev, [key]: e.target.value }))
              }
              placeholder="0"
              className={inputClass}
            />
          </div>
        ))}
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-foreground">
          Labor Rate ($/hr)
        </label>
        <input
          type="number"
          value={laborRate}
          onChange={(e) => setLaborRate(e.target.value)}
          placeholder="75"
          className={`${inputClass} max-w-[200px]`}
        />
      </div>

      {hasResult && (
        <>
          <div className="mt-8 rounded-lg border border-border bg-muted/20 p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Estimate Summary
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="text-sm text-muted-foreground">Pipe Material</div>
                <div className="text-xl font-bold text-foreground">
                  ${fmt(pipeCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmt(length)} ft + {waste}% waste
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Fittings</div>
                <div className="text-xl font-bold text-foreground">
                  ${fmt(fittingsCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fittings} fittings @ ${avgFittingCost.toFixed(2)} avg
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Labor</div>
                <div className="text-xl font-bold text-foreground">
                  ${fmt(laborCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fmtDec(totalLaborHours)} hrs @ ${fmt(rate)}/hr
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total</div>
                <div className="text-2xl font-bold text-secondary">
                  ${fmt(totalCost)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Before markup and overhead
                </div>
              </div>
            </div>
          </div>
          <ResultActions
            toolSlug="plumbing-calculator"
            inputs={{ pipeType, pipeLength, fittingsCount, laborRate, wasteFactor }}
            outputs={{ materialCost, laborCost, totalCost, totalLaborHours }}
          />
        </>
      )}
    </div>
  );
}
